import React, { useState } from 'react';
import { Table, Button, Space, notification, Typography, Tag, Tooltip } from 'antd';
import {
  DownloadOutlined,
  DeleteOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  UserOutlined,
  SendOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import useAuth from '../../hooks/useAuth';
import notificationApi from '../../utils/notificationApi';
import { deleteVoucherApi } from '../../api/attentionVoucher';
import { useActionsComprobantes } from '../../providers/ActionsComprobantesProvider';
import SendVoucherModal from '../../components/specialist/AttentionVoucher/SendVoucherModal';
import DocumentDeliveryDrawer from '../../components/global/DocumentDeliveryDrawer';

function VoucherTable(props) {
  const { vouchers, setReload } = props;
  const { user = { fullname: 'nombre especialista' } } = useAuth();
  const { dowload } = useActionsComprobantes();
  const [sendModal, setSendModal] = useState({ open: false, voucher: null });
  const [historialDrawer, setHistorialDrawer] = useState({ open: false, entidadId: null });

  /**
   * Función que alterna el estado de un comprobante de atención
   * @param {Number} voucherId - Id del voucher a modificar su estado
   */
  const toggleActivatedVoucherApi = async (voucherId, refreshVouchers) => {
    const result = await deleteVoucherApi(voucherId);
    if (notificationApi(result)) {
      refreshVouchers(true);
    }
  };

  /**
   * Función que alterna el estado de un comprobante de atención
   * @param {Number} voucherId - Id del voucher a modificar su estado
   */
  const toggleActivatedVoucher = (voucherId, refreshVouchers) => {
    notification.open({
      message: `¿Seguro que desea desactivar el comprobante ${voucherId}?`,
      btn: (
        <Button
          danger
          type="primary"
          onClick={() => toggleActivatedVoucherApi(voucherId, refreshVouchers)}
        >
          Desactivar
        </Button>
      ),
    });
  };

  /**
   * Dispara una notificación con un botón que permite descargar el comprobante
   * @param {Object} user - Datos del usuario loggeado
   * @param {Object} voucher - Datos del voucher a descargar
   */
  const showDownloadVoucherNotification = (user, voucher) => {
    dowload(voucher.id);
    // notification.open({
    //   message: 'Descargar Comprobante',
    //   btn: <Dowload user={user} voucher={voucher} />,
    // });
  };

  /**
   * Acción de botón que permite disparar la notificación
   * con el botón para descargar el comprobante
   * @param {Object} voucher
   */
  const buttonActionDowloadVoucher = async (voucher) => {
    await showDownloadVoucherNotification(user, voucher);
  };

  /**
   * Acción de botón que permite alternar el estado del
   * comprobante
   * @param {Object} voucher
   */
  const buttonActionToggleActivatedVoucher = (voucher) => {
    toggleActivatedVoucher(voucher.id, setReload);
  };

  /**
   * Columnas para la tabla con diseño moderno
   */
  const columns = [
    {
      title: (
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
          <span className="font-medium">ID</span>
        </div>
      ),
      key: 'id',
      dataIndex: 'id',
      width: 80,
      render: (id) => (
        <Typography.Text strong className="text-blue-600 font-semibold">
          #{id}
        </Typography.Text>
      ),
      sorter: (a, b) => a.id - b.id,
      defaultSortOrder: 'descend',
    },
    {
      title: (
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          <span className="font-medium">Edificio</span>
        </div>
      ),
      key: 'client_name',
      dataIndex: 'client_name',
      width: 200,
      render: (clientName) => (
        <Typography.Text className="text-gray-700 capitalize" ellipsis={{ tooltip: clientName }}>
          {clientName}
        </Typography.Text>
      ),
    },
    {
      title: (
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
          <span className="font-medium">Equipos</span>
        </div>
      ),
      key: 'equipment',
      dataIndex: 'equipment',
      width: 200,
      render: (equipment) => (
        <Typography.Text className="text-sm" ellipsis={{ tooltip: equipment }}>
          {equipment}
        </Typography.Text>
      ),
    },
    {
      title: (
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
          <span className="font-medium">Inicio</span>
        </div>
      ),
      key: 'attention_init',
      dataIndex: 'attention_init',
      width: 120,
      render: (time) => (
        <div className="flex items-center space-x-1">
          <ClockCircleOutlined className="text-purple-500 text-xs" />
          <Typography.Text className="text-sm">{time}</Typography.Text>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
          <span className="font-medium">Fin</span>
        </div>
      ),
      key: 'attention_finish',
      dataIndex: 'attention_finish',
      width: 120,
      render: (time) => (
        <div className="flex items-center space-x-1">
          <ClockCircleOutlined className="text-indigo-500 text-xs" />
          <Typography.Text className="text-sm">{time}</Typography.Text>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
          <span className="font-medium">Estado Final</span>
        </div>
      ),
      key: 'finish_state',
      dataIndex: 'finish_state',
      width: 150,
      render: (state) => (
        <Tag
          color={state === 'operativo' ? 'green' : state === 'mantenimiento' ? 'orange' : 'red'}
          className="font-medium px-3 py-1 rounded-full"
        >
          {state}
        </Tag>
      ),
    },
    {
      title: (
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-pink-500 rounded-full"></div>
          <span className="font-medium">Recepcionista</span>
        </div>
      ),
      key: 'recepcionist_name',
      dataIndex: 'recepcionist_name',
      width: 150,
      render: (name) => (
        <div className="flex items-center space-x-1">
          <UserOutlined className="text-pink-500 text-xs" />
          <Typography.Text className="text-sm capitalize" ellipsis={{ tooltip: name }}>
            {name}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: (
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
          <span className="font-medium">Acciones</span>
        </div>
      ),
      fixed: 'right',
      width: 160,
      align: 'center',
      render: (record) => (
        <Space size="small">
          <Tooltip title="Historial de entregas">
            <Button
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => setHistorialDrawer({ open: true, entidadId: record.id })}
            />
          </Tooltip>
          <Tooltip title="Enviar comprobante">
            <Button
              type="default"
              size="small"
              icon={<SendOutlined />}
              onClick={() => setSendModal({ open: true, voucher: record })}
            />
          </Tooltip>
          <Tooltip title="Descargar comprobante">
            <Button
              type="primary"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => buttonActionDowloadVoucher(record)}
              className="bg-blue-600 hover:bg-blue-700 border-blue-600"
            />
          </Tooltip>
          <Tooltip title="Eliminar comprobante">
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => buttonActionToggleActivatedVoucher(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={vouchers}
        scroll={{ x: 1000 }}
        size="middle"
        className="modern-table"
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} comprobantes`,
          pageSizeOptions: ['10', '20', '50', '100'],
          defaultPageSize: 20,
        }}
        locale={{
          emptyText: (
            <div className="text-center py-8">
              <FileTextOutlined className="text-4xl text-gray-400 mb-2" />
              <p className="text-gray-500">No hay comprobantes registrados</p>
            </div>
          ),
        }}
      />
      <SendVoucherModal
        open={sendModal.open}
        voucher={sendModal.voucher}
        onClose={() => setSendModal({ open: false, voucher: null })}
      />
      <DocumentDeliveryDrawer
        open={historialDrawer.open}
        onClose={() => setHistorialDrawer({ open: false, entidadId: null })}
        tipo="comprobante"
        entidadId={historialDrawer.entidadId}
        titulo={historialDrawer.entidadId ? `Comprobante #${historialDrawer.entidadId}` : ''}
      />
    </>
  );
}

export default VoucherTable;
